import * as Yup from 'yup';
import { AppRegistrationSection } from '../../AppRegistrationSection';

import { ButtonActor, InputActor, Spacer, Text, Title } from 'components';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getScreenTranslation } from '../../utils/translations';
import { mobilePhoneNumberValid } from '../../utils/regularExpressions';
import { phoneSanitizer } from '../../utils/sanitizers';
import { useCallback, useTransition } from 'react';
import { useErrorManager } from 'InternalLibrary';
import { useFormik } from 'formik';

import { usePhoneVerificationScreenLogic } from '../PhoneVerification/usePhoneVerificationScreenLogic';
import { useTranslation } from 'react-i18next';
import layout from 'scss/layout.module.scss';
import styles from './PhoneNumberChange.module.scss';

export const SESSION_PHONE_UPDATED_KEY = 'phoneUpdated';

export default function PhoneVerificationChangePhone() {
  const i18Next = useTranslation();
  const [loading, startLoading] = useTransition();
  const { setError } = useErrorManager();
  const { updateClientPhone } = usePhoneVerificationScreenLogic();

  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .required(i18Next.t('validation:required_field'))
      .test('wrong_format', i18Next.t('validation:wrong_format'), (value) =>
        mobilePhoneNumberValid(value ?? ''),
      ),
  });

  const onSubmit = useCallback(
    async (data: { phoneNumber: string }) => {
      formik.setStatus({ errors: {} });
      startLoading(async () => {
        await updateClientPhone(data.phoneNumber);
      });
    },
    [updateClientPhone, setError],
  );

  const formik = useFormik({
    initialValues: {
      phoneNumber: '',
    },
    validationSchema,
    onSubmit,
  });

  const content = getScreenTranslation('phoneChange', i18Next);

  return (
    <Frame
      {...frameConfig}
      sectionID={AppRegistrationSection.Identification}
      hideEditButton
    >
      <div className={layout['width-sm']}>
        <Title text={content.heading} />
        <Spacer marginTop="23px" marginBottom="15px">
          <Text
            text={content.subheading}
            level={2}
            kind="p"
            className={styles.subheading}
            dataTest="phoneNumberChange-subheading"
          />
        </Spacer>

        <form onSubmit={formik.handleSubmit} data-test="form">
          <InputActor
            formik={formik}
            name="phoneNumber"
            label={content.mobile_phone}
            sanitizer={phoneSanitizer}
            analytics={{
              dontTrackContent: true,
            }}
            inputProps={{
              allowSpaces: false,
            }}
            fieldGroupProps={{
              dataTestValidationText: 'validationText',
            }}
          />

          <ButtonActor
            buttonProps={{
              className: styles['continue-button'],
              dataTest: 'phoneNumberChange-continue-btn',
              type: 'submit',
              loading,
            }}
            formik={formik}
          >
            {content.continue_button}
          </ButtonActor>
        </form>
      </div>
    </Frame>
  );
}
